import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { IEmergencia } from 'modelos/src';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  MapCardComponent,
  MapCardConfig,
} from '../../../../../componentes/map-card/map-card.component';
import {
  MapaFacilData,
  MarkerMapa,
} from '../../../../../auxiliares/mapa-facil/interfaces';
import { AmbulanciasLayerManager } from '../../../../../auxiliares/mapbox/entity-managers/ambulancias-layer.manager';
import { EmergenciasMedicasLayerManager } from '../../../../../auxiliares/mapbox/entity-managers/emergencias-medicas-layer.manager';
import { CardHeaderComponent } from '../../../../../componentes/card-header/card-header.component';
import { ButtonComponent } from '../../../../../auxiliares/button/button.component';
import {
  IMovilEmergencia,
  distanciaFormateada,
} from '../acciones-emergencia/movil-emergencia';

export interface ISelectorMovilEmergenciaData {
  emergencia?: IEmergencia;
  /** Ambulancias ya cargadas por el diálogo de asignación. */
  moviles: IMovilEmergencia[];
  idSeleccionado?: string | null;
}

/**
 * Elegir la ambulancia en el mapa. Muestra la emergencia (con un radio de
 * 200 m) y las ambulancias con su último reporte; las que están en otra
 * emergencia activa se ven pero no se pueden elegir. Devuelve el móvil elegido.
 */
@Component({
  selector: 'app-selector-movil-emergencia',
  templateUrl: './selector-movil-emergencia.component.html',
  styleUrls: [
    './selector-movil-emergencia.component.scss',
    '../acciones-emergencia/acciones-emergencia-dialog.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatCardModule,
    MatDividerModule,
    MatIconModule,
    MapCardComponent,
    CardHeaderComponent,
    ButtonComponent,
  ],
})
export class SelectorMovilEmergenciaComponent implements OnInit, OnDestroy {
  private helper = inject(HelperService);
  private cdr = inject(ChangeDetectorRef);
  private ambulanciasLayerManager = inject(AmbulanciasLayerManager);
  private emergenciasMedicasLayerManager = inject(
    EmergenciasMedicasLayerManager,
  );
  private dialogRef = inject(MatDialogRef<SelectorMovilEmergenciaComponent>);
  public data = inject<ISelectorMovilEmergenciaData>(MAT_DIALOG_DATA);

  @ViewChild(MapCardComponent) mapCardComponent?: MapCardComponent;

  public mapCardConfig?: MapCardConfig;
  public movilSeleccionado?: IMovilEmergencia;
  public distanciaFormateada = distanciaFormateada;

  public get disponibles(): IMovilEmergencia[] {
    return this.data.moviles.filter((m) => !m.disabled);
  }

  public get ocupados(): IMovilEmergencia[] {
    return this.data.moviles.filter((m) => m.disabled);
  }

  public get sinPosicion(): number {
    return this.data.moviles.filter((m) => !m.posicion?.coordinates).length;
  }

  private generarMapa(): void {
    const markers: MarkerMapa[] = [];
    for (const movil of this.data.moviles) {
      const coords = movil.posicion?.coordinates;
      if (!coords || !movil.ultimoReporte) continue;
      markers.push({
        label: 'ambulancia',
        coor: coords as [number, number],
        text:
          this.movilSeleccionado?._id === movil._id
            ? movil.identificacion
            : undefined,
        id: movil.ultimoReporte._id,
        data: {
          ...movil.ultimoReporte,
          idActivo: movil._id,
          asignada: !!movil.disabled,
          color: movil.colorComunicacion,
        },
      });
    }

    const destino = this.data.emergencia?.ubicacionDestino?.coordinates as
      | [number, number]
      | undefined;
    if (destino) {
      markers.push({
        label: 'emergenciaMedica',
        coor: destino,
        text: 'Emergencia',
        centrar: true,
        data: { ...this.data.emergencia },
      });
    }

    const mapData: MapaFacilData = {
      markers,
      center: destino ?? markers[0]?.coor ?? [0, 0],
      zoom: destino ? 15 : 13,
      selectorBaseLayer: true,
      clustering: { enabled: false },
    };

    this.mapCardConfig = {
      title: 'Mapa',
      icon: 'map',
      color: 'blue',
      mapOptions: mapData,
      nombreMap: 'emergenciasSelector',
      disablePopups: true,
    };

    if (destino) {
      setTimeout(() => this.dibujarCirculoEmergencia(), 1500);
    }
  }

  /** Radio de 200 m alrededor del lugar de la emergencia. */
  private dibujarCirculoEmergencia(intentos = 0): void {
    const destino = this.data.emergencia?.ubicacionDestino?.coordinates;
    if (!destino) return;
    const map = this.mapCardComponent?.getMap();
    if (!map) {
      if (intentos < 10) {
        setTimeout(() => this.dibujarCirculoEmergencia(intentos + 1), 500);
      }
      return;
    }
    const dibujar = (m: mapboxgl.Map) => {
      this.emergenciasMedicasLayerManager.setMapForCircle(m);
      this.emergenciasMedicasLayerManager.dibujarCirculoDeRadio(
        destino as [number, number],
        200,
      );
    };
    if (map.loaded()) dibujar(map);
    else map.once('load', () => dibujar(map));
  }

  public onMarkerClick(entity: any): void {
    if (!entity) return;
    const idReporte = entity?.id ?? entity?.properties?.id;
    const movil = this.data.moviles.find(
      (m) => m.ultimoReporte?._id === idReporte,
    );
    if (movil) this.seleccionar(movil);
  }

  public seleccionar(movil: IMovilEmergencia): void {
    if (movil.disabled) {
      this.helper.notifWarn(
        `${movil.identificacion} está asignada a la emergencia ${movil.asignadaA}`,
      );
      return;
    }
    this.movilSeleccionado = movil;
    this.ambulanciasLayerManager.mostrarNombreDeAmbulancia(movil._id!);
    const coords = movil.posicion?.coordinates;
    const map = this.mapCardComponent?.getMap();
    if (coords && map) {
      map.flyTo({
        center: coords as [number, number],
        zoom: 16,
        duration: 1500,
      });
    }
    this.cdr.markForCheck();
  }

  public confirmar(): void {
    if (this.movilSeleccionado) this.dialogRef.close(this.movilSeleccionado);
  }

  public cerrar(): void {
    this.dialogRef.close();
  }

  ngOnInit(): void {
    if (this.data.idSeleccionado) {
      this.movilSeleccionado = this.data.moviles.find(
        (m) => m._id === this.data.idSeleccionado && !m.disabled,
      );
    }
    this.generarMapa();
  }

  ngOnDestroy(): void {
    this.ambulanciasLayerManager.mostrarNombreDeAmbulancia(null);
    this.emergenciasMedicasLayerManager.limpiarCirculoDeRadio();
  }
}
